// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import { type ReactNode, useRef, useState } from "react";
import {
  type ImperativePanelGroupHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";

import { ResizeHandle } from "../../components/ResizeHandle";
import { useContainerWidth } from "../../hooks/useContainerWidth";
import { usePersistedLayout } from "../../hooks/usePersistedLayout";

/** Same split as the v1 project review: chat | artwork. */
const REVIEW_PANE_DEFAULT: number[] = [36, 64];
const REVIEW_PANE_MINS: number[] = [18, 40];
const REVIEW_PANE_STORAGE_KEY = "ask-v2-review-panes";

// why: 768 matches v1 RESIZE_LAYOUT_MIN_WIDTH_PX — percentage panels below that crush the findings column
const REVIEW_RESIZE_MIN_WIDTH_PX = 768;

export function AskV2ReviewPanes({
  left,
  right,
  abortSignal,
}: {
  left: ReactNode;
  right: ReactNode;
  abortSignal?: string | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(containerRef);
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
  const [dragInProgress, setDragInProgress] = useState(false);
  const persisted = usePersistedLayout({
    storageKey: REVIEW_PANE_STORAGE_KEY,
    defaultLayout: REVIEW_PANE_DEFAULT,
    minSizes: REVIEW_PANE_MINS,
  });
  const resizable =
    containerWidth != null && containerWidth >= REVIEW_RESIZE_MIN_WIDTH_PX;

  return (
    <div
      ref={containerRef}
      className="flex size-full min-h-0 min-w-0 flex-1 overflow-hidden"
      data-testid="ask-v2-review-panes"
    >
      {resizable ? (
        <PanelGroup
          direction="horizontal"
          ref={panelGroupRef}
          onLayout={persisted.onLayout}
          className={`flex flex-1 ${dragInProgress ? "[&_*]:!transition-none" : ""}`}
        >
          <Panel
            id="chat"
            order={0}
            minSize={REVIEW_PANE_MINS[0]}
            defaultSize={persisted.layout[0]}
          >
            <div
              className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
              data-testid="ask-v2-review-side"
            >
              {left}
            </div>
          </Panel>
          <ResizeHandle
            id="chat-right"
            panelGroupRef={panelGroupRef}
            defaultLayout={REVIEW_PANE_DEFAULT}
            abortSignal={abortSignal}
            onDraggingChange={setDragInProgress}
          />
          <Panel
            id="artwork"
            order={1}
            minSize={REVIEW_PANE_MINS[1]}
            defaultSize={persisted.layout[1]}
          >
            <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
              {right}
            </div>
          </Panel>
        </PanelGroup>
      ) : (
        <>
          <div
            className="flex h-full min-h-0 min-w-[280px] max-w-[420px] flex-[1] flex-col overflow-hidden border-r border-puntt-neutral-gray-5"
            data-testid="ask-v2-review-side"
          >
            {left}
          </div>
          <div className="flex min-h-0 min-w-0 flex-[3] flex-col overflow-hidden">
            {right}
          </div>
        </>
      )}
    </div>
  );
}
